import { User, SignUpParams, SignInParams, AuthError, Plan } from './types';
import type { VisaPreparation, ConsultingOffer } from '@preparation';
import { ApiError, fetchJson, responseJson } from '@/shared/api';
import type { WireRequest, WireResponse } from '@/shared/generated/wire';
import { routes, apiRoutes } from '@/shared/routes';
import { attribution, markAdClicksReported, unreportedAttribution } from '@/shared/analytics/adClicks';
import type { OAuthIdentity } from './oauth/oauth';

interface AuthJsonResponse {
  user?: User;
  error?: string;
  errors?: Record<string, string[]>;
}

export type PaymentGateway = 'stripe' | 'mercadopago';

export interface MeResponse {
  user: User;
  plan: Plan;
  gateway: PaymentGateway | null;
  visa_preparation: VisaPreparation | null;
  consulting: ConsultingOffer | null;
}

export async function getMe(): Promise<MeResponse | null> {
  const response = await fetchJson(apiRoutes.me);
  if (!response.ok) return null;
  const wire = await responseJson<WireResponse<'/app/api/me', 'get', 200>>(response);
  if (!wire.user) return null;
  if (!wire.plan) throw new Error('Expected a plan for the authenticated customer');
  return {
    ...wire,
    user: toUser(wire.user),
    plan: { ...wire.plan, schedule_cost_cents: wire.plan.schedule_cost_cents ?? null },
  };
}

function toUser(wire: WireResponse<'/app/users/session', 'post', 200>['user']): User {
  return {
    ...wire,
    name: wire.name ?? null,
    first_name: wire.first_name ?? null,
    last_name: wire.last_name ?? null,
    country: wire.country ?? null,
    locale: wire.locale ?? null,
  };
}

export async function reportAdClicks(): Promise<void> {
  const pending = unreportedAttribution();
  if (!pending) return;

  const response = await fetchJson(apiRoutes.adClicks, {
    method: 'POST',
    data: { attribution: pending } satisfies WireRequest<'/app/api/ad_clicks', 'post'>,
  });
  if (response.ok) markAdClicksReported();
}

export async function signIn(params: SignInParams): Promise<User> {
  const response = await fetchJson(routes.auth.session, { method: 'POST', data: params satisfies WireRequest<'/app/users/session', 'post'> });
  if (!response.ok) {
    const body = (await response.json()) as AuthJsonResponse;
    throw new AuthError({ error: body.error });
  }
  const data = await responseJson<WireResponse<'/app/users/session', 'post', 200>>(response);
  return toUser(data.user);
}

export async function signUp(params: SignUpParams): Promise<User> {
  const response = await fetchJson(routes.auth.signUp, {
    method: 'POST',
    data: { ...params, attribution: attribution() } satisfies WireRequest<'/app/users/sign_up', 'post'>,
  });
  if (!response.ok) {
    const body = (await response.json()) as AuthJsonResponse;
    throw new AuthError({ error: body.error, fieldErrors: body.errors });
  }
  const data = await responseJson<WireResponse<'/app/users/sign_up', 'post', 201>>(response);
  markAdClicksReported();
  return toUser(data.user);
}

export async function signOut(): Promise<void> {
  await fetchJson(routes.auth.session, { method: 'DELETE' });
}

export interface SignUpWithOAuthParams {
  oauth_token: string;
  name: string;
  country: string;
  phone?: string;
  terms_accepted: boolean;
}

export async function signUpWithOAuth(params: SignUpWithOAuthParams): Promise<User> {
  const response = await fetchJson(routes.auth.signUpWithOAuth, {
    method: 'POST',
    data: { ...params, attribution: attribution() } satisfies WireRequest<'/app/users/sign_up/with_oauth', 'post'>,
  });
  if (!response.ok) {
    const body = (await response.json()) as AuthJsonResponse;
    throw new AuthError({ error: body.error, fieldErrors: body.errors });
  }
  const data = await responseJson<WireResponse<'/app/users/sign_up/with_oauth', 'post', 201>>(response);
  markAdClicksReported();
  return toUser(data.user);
}

export async function fetchPendingOAuthIdentity(): Promise<OAuthIdentity | null> {
  const response = await fetchJson(apiRoutes.oauthPending);
  if (response.status === 204) return null;
  if (!response.ok) throw new ApiError(response.status, `Pending OAuth identity failed: ${response.status}`);

  const data = await responseJson<WireResponse<'/app/users/oauth/pending', 'get', 200>>(response);
  return { ...data.identity, provider: oauthProvider(data.identity.provider) };
}

function oauthProvider(value: string): OAuthIdentity['provider'] {
  switch (value) {
    case 'google_oauth2':
    case 'microsoft_graph':
    case 'facebook': {
      return value;
    }
    default: {
      throw new Error(`Expected an OAuth provider, received ${value}`);
    }
  }
}

export async function discardPendingOAuthIdentity(): Promise<void> {
  await fetchJson(apiRoutes.oauthPending, { method: 'DELETE' });
}

export async function resendEmailVerification(email: string): Promise<void> {
  await fetchJson(routes.auth.emailVerificationResend, {
    method: 'POST',
    data: { email } satisfies WireRequest<'/app/users/email_verification/resend', 'post'>,
  });
}

export async function confirmEmailVerification(token: string): Promise<void> {
  const response = await fetchJson(routes.auth.emailVerification, {
    method: 'POST',
    data: { token } satisfies WireRequest<'/app/users/email_verification', 'post'>,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as AuthJsonResponse;
    throw new AuthError({ error: body.error });
  }
}

export async function requestPasswordReset(email: string): Promise<void> {
  const response = await fetchJson(routes.auth.passwordReset, { method: 'POST', data: { email } satisfies WireRequest<'/app/users/password_reset', 'post'> });
  if (!response.ok) {
    const body = (await response.json()) as AuthJsonResponse;
    throw new AuthError({ error: body.error });
  }
}

export async function confirmPasswordReset(token: string, password: string): Promise<void> {
  const response = await fetchJson(routes.auth.passwordReset, {
    method: 'PUT',
    data: { token, password } satisfies WireRequest<'/app/users/password_reset', 'put'>,
  });
  if (!response.ok) {
    const body = (await response.json()) as AuthJsonResponse;
    throw new AuthError({ error: body.error });
  }
}

export interface ChangePasswordParams {
  currentPassword: string;
  newPassword: string;
}

export async function changePassword(params: ChangePasswordParams): Promise<void> {
  const response = await fetchJson(routes.auth.password, {
    method: 'PUT',
    data: {
      current_password: params.currentPassword,
      password: params.newPassword,
    } satisfies WireRequest<'/app/users/password', 'put'>,
  });
  if (!response.ok) {
    const body = (await response.json()) as AuthJsonResponse;
    throw new AuthError({ error: body.error });
  }
}
